import type { ProductConfig } from '@/types/product';
import type { Question, ScoringCategory } from '@/types';

// Pontuação mínima possível ≈ 20 (1 pt por questão, modelo educativo sem punição)
// Pontuação máxima = 95 pts (q1 é texto livre, sem pontuação)

const SELECT_QUESTIONS: Question[] = [

  // ============================
  // BLOCO 1 — ESTRUTURA FINANCEIRA (20 pts)
  // ============================

  {
    id: 'sel_fin_1',
    category: 'estrutura_financeira',
    text: 'Qual valor você pretende investir em uma franquia? (Informe o valor aproximado)',
    type: 'currency',
    weight: 0,
    maxScore: 0,
    required: true
  },
  {
    id: 'sel_fin_2',
    category: 'estrutura_financeira',
    text: 'Esse valor representa quanto do seu patrimônio disponível?',
    type: 'single',
    options: [
      { value: 'ate_30', label: 'Até 30% do meu patrimônio disponível', score: 6 },
      { value: '30_a_60', label: 'Entre 30% e 60% do meu patrimônio', score: 3 },
      { value: 'acima_60', label: 'Acima de 60% do meu patrimônio', score: 1 }
    ],
    weight: 6,
    maxScore: 6,
    required: true
  },
  {
    id: 'sel_fin_3',
    category: 'estrutura_financeira',
    text: 'Você possui reserva pessoal para se manter enquanto o negócio amadurece?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, tenho reserva separada', score: 6 },
      { value: 'parcial', label: 'Parcialmente', score: 3 },
      { value: 'nao', label: 'Ainda não tenho reserva', score: 1 }
    ],
    weight: 6,
    maxScore: 6,
    required: true
  },
  {
    id: 'sel_fin_4',
    category: 'estrutura_financeira',
    text: 'Você entende que o valor total de investimento pode variar conforme modelo, estrutura e capital de giro necessário?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, tenho clareza sobre isso', score: 4 },
      { value: 'iniciando', label: 'Estou começando a entender', score: 2 },
      { value: 'nao', label: 'Ainda não tinha pensado nisso', score: 1 }
    ],
    weight: 4,
    maxScore: 4,
    required: true
  },
  {
    id: 'sel_fin_5',
    category: 'estrutura_financeira',
    text: 'Você já ouviu falar em ponto de equilíbrio do negócio?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, sei o que é e como calcular', score: 4 },
      { value: 'ja_ouvi', label: 'Já ouvi, mas não sei calcular', score: 2 },
      { value: 'nao', label: 'Ainda não', score: 1 }
    ],
    weight: 4,
    maxScore: 4,
    required: true
  },

  // ============================
  // BLOCO 2 — ENTENDIMENTO DO MODELO DE FRANQUIA (20 pts)
  // ============================

  {
    id: 'sel_mod_6',
    category: 'entendimento_modelo',
    text: 'Você sabe a diferença entre faturamento e lucro?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, claramente', score: 5 },
      { value: 'nocao', label: 'Tenho noção', score: 3 },
      { value: 'nao', label: 'Ainda não', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_mod_7',
    category: 'entendimento_modelo',
    text: 'Você sabe que a projeção de DRE é diferente da DRE real de uma unidade em operação?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, tenho essa clareza', score: 5 },
      { value: 'iniciando', label: 'Estou começando a entender', score: 3 },
      { value: 'nao', label: 'Ainda não', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_mod_8',
    category: 'entendimento_modelo',
    text: 'Você entende como royalties e taxa de marketing impactam o lucro final do franqueado?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, entendo o impacto', score: 5 },
      { value: 'parcial', label: 'Parcialmente', score: 3 },
      { value: 'nao', label: 'Ainda não', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_mod_9',
    category: 'entendimento_modelo',
    text: 'Você já conversou com algum franqueado antes de pensar em investir?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, já conversei com franqueados', score: 5 },
      { value: 'pretendo', label: 'Ainda não, mas pretendo', score: 3 },
      { value: 'nao_pensei', label: 'Ainda não tinha pensado nisso', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },

  // ============================
  // BLOCO 3 — CONSCIÊNCIA JURÍDICA (20 pts)
  // ============================

  {
    id: 'sel_jur_10',
    category: 'consciencia_juridica',
    text: 'Você já ouviu falar da Lei de Franquias (Lei 13.966/2019)?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, conheço a lei', score: 5 },
      { value: 'ja_ouvi', label: 'Já ouvi falar', score: 3 },
      { value: 'nao', label: 'Ainda não', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_jur_11',
    category: 'consciencia_juridica',
    text: 'Você sabe o que é a COF (Circular de Oferta de Franquia)?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, sei o que é', score: 5 },
      { value: 'ja_ouvi', label: 'Já ouvi falar', score: 3 },
      { value: 'nao', label: 'Ainda não', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_jur_12',
    category: 'consciencia_juridica',
    text: 'Você pretende analisar o contrato com apoio jurídico antes de assinar?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, com certeza', score: 5 },
      { value: 'provavelmente', label: 'Provavelmente', score: 3 },
      { value: 'nao_pensou', label: 'Ainda não tinha pensado nisso', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_jur_13',
    category: 'consciencia_juridica',
    text: 'Você entende que o contrato pode ter cláusulas sobre território, fornecedores e regras operacionais?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, entendo essas implicações', score: 5 },
      { value: 'iniciando', label: 'Estou começando a entender', score: 3 },
      { value: 'nao', label: 'Ainda não', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },

  // ============================
  // BLOCO 4 — CAPACIDADE DE ANÁLISE E VALIDAÇÃO (20 pts)
  // ============================

  {
    id: 'sel_ana_14',
    category: 'capacidade_analise',
    text: 'Você pretende visitar unidades da franquia antes de decidir?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, com certeza', score: 5 },
      { value: 'provavelmente', label: 'Provavelmente', score: 3 },
      { value: 'nao_pensou', label: 'Ainda não tinha pensado nisso', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_ana_15',
    category: 'capacidade_analise',
    text: 'Você pretende conversar com mais de um franqueado antes de decidir?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, vou ouvir diferentes opiniões', score: 5 },
      { value: 'provavelmente', label: 'Provavelmente', score: 3 },
      { value: 'nao', label: 'Ainda não tinha pensado nisso', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_ana_16',
    category: 'capacidade_analise',
    text: 'Você acredita que contar com uma análise especializada pode ajudar na tomada de decisão?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, definitivamente', score: 5 },
      { value: 'talvez', label: 'Talvez', score: 3 },
      { value: 'nao_sei', label: 'Ainda não sei', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_ana_17',
    category: 'capacidade_analise',
    text: 'Você entende que cada modelo de franquia exige um perfil diferente (operador, gestor, investidor)?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, entendo as diferenças', score: 5 },
      { value: 'parcial', label: 'Parcialmente', score: 3 },
      { value: 'nao', label: 'Ainda não', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },

  // ============================
  // BLOCO 5 — PERFIL & MOMENTO DE VIDA (15 pts)
  // ============================

  {
    id: 'sel_per_18',
    category: 'perfil_momento_vida',
    text: 'Você está disposto a se dedicar intensamente nos primeiros meses do negócio?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, estou preparado', score: 5 },
      { value: 'parcial', label: 'Parcialmente', score: 3 },
      { value: 'avaliando', label: 'Ainda estou avaliando', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_per_19',
    category: 'perfil_momento_vida',
    text: 'Você já refletiu sobre seus objetivos com a franquia (renda, independência, expansão futura)?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, tenho objetivos claros', score: 5 },
      { value: 'parcial', label: 'Parcialmente', score: 3 },
      { value: 'nao', label: 'Ainda não refleti sobre isso', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  },
  {
    id: 'sel_per_20',
    category: 'perfil_momento_vida',
    text: 'Você acredita que franquia exige disciplina e respeito ao padrão da marca?',
    type: 'single',
    options: [
      { value: 'sim', label: 'Sim, entendo e aceito isso', score: 5 },
      { value: 'parcial', label: 'Parcialmente', score: 3 },
      { value: 'nao', label: 'Ainda não', score: 1 }
    ],
    weight: 5,
    maxScore: 5,
    required: true
  }
];

// Max total = 6+6+4+4 (bloco1) + 5×4 (bloco2) + 5×4 (bloco3) + 5×4 (bloco4) + 5×3 (bloco5)
// = 20 + 20 + 20 + 20 + 15 = 95 pts
const SELECT_MAX_TOTAL = 95;

const questionCategories = Object.fromEntries(
  SELECT_QUESTIONS.map(q => [q.id, q.category as ScoringCategory])
) as Record<string, ScoringCategory>;

export const selectConfig: ProductConfig = {
  id: 'select',
  title: 'Inova Select — Compra de Franquias',
  description: 'Descubra qual franquia combina com seu perfil e capital. Nossa consultoria conecta investidores aos melhores negócios do Brasil.',
  funilId: 71,
  crmOrigem: 'Diagnóstico Select',
  questions: SELECT_QUESTIONS,
  pdf: 'select',
  scoring: {
    categories: [
      'estrutura_financeira',
      'entendimento_modelo',
      'consciencia_juridica',
      'capacidade_analise',
      'perfil_momento_vida'
    ],
    weights: {
      estrutura_financeira: 20,
      entendimento_modelo: 20,
      consciencia_juridica: 20,
      capacidade_analise: 20,
      perfil_momento_vida: 15
    },
    labels: {
      estrutura_financeira: 'Estrutura Financeira',
      entendimento_modelo: 'Entendimento do Modelo',
      consciencia_juridica: 'Consciência Jurídica',
      capacidade_analise: 'Capacidade de Análise',
      perfil_momento_vida: 'Perfil & Momento de Vida'
    },
    maxTotal: SELECT_MAX_TOTAL,
    improvements: {
      estrutura_financeira: 1.0,
      entendimento_modelo: 1.0,
      consciencia_juridica: 1.0,
      capacidade_analise: 1.0,
      perfil_momento_vida: 1.0
    },
    nonScoringCategories: [],
    questionCategories,
    scoreThresholds: [85, 48], // 81-95 → alto; 46-80 → médio; <48 → baixo
    finalRecommendations: [
      'Perfil Avançado — você está acima da média dos investidores',
      'Preparação Consistente — boas bases, alguns ajustes ampliam sua segurança',
      'Início de Jornada — orientação adequada vai acelerar sua preparação'
    ],
    recommendationMaps: {
      specific: {
        sel_fin_2: {
          acima_60: 'Investir acima de 60% do patrimônio aumenta o risco. Nossa consultoria ajuda a encontrar opções mais adequadas ao seu perfil financeiro.'
        },
        sel_fin_3: {
          nao: 'Ter uma reserva pessoal é fundamental para atravessar os primeiros meses do negócio com tranquilidade.'
        },
        sel_jur_12: {
          nao_pensou: 'Analisar o contrato com um advogado especializado em franquias é um passo essencial antes de assinar qualquer documento.'
        }
      },
      general: {
        estrutura_financeira: {
          low: 'Sua estrutura financeira ainda precisa de organização antes de investir em uma franquia. Nossa consultoria ajuda a traçar o melhor caminho.',
          medium: 'Você possui boa base financeira. Com alguns ajustes, estará ainda mais preparado para o investimento.',
          high: 'Excelente estrutura financeira! Você tem os recursos e a consciência necessária para investir com segurança.'
        },
        entendimento_modelo: {
          low: 'Aprofundar o entendimento sobre o modelo de franquias vai aumentar muito sua segurança na decisão de investir.',
          medium: 'Você já demonstra boa compreensão do modelo. Continue se aprofundando para fazer uma escolha ainda mais informada.',
          high: 'Ótimo entendimento do modelo de franquias! Você está bem preparado para analisar as oportunidades disponíveis.'
        },
        consciencia_juridica: {
          low: 'Conhecer os aspectos jurídicos é fundamental para proteger seu investimento. Nossa equipe pode orientá-lo nesse processo.',
          medium: 'Você tem consciência jurídica razoável. Ampliar esse conhecimento vai trazer mais segurança na negociação.',
          high: 'Excelente consciência jurídica! Você sabe da importância de analisar contratos e documentos antes de fechar negócio.'
        },
        capacidade_analise: {
          low: 'Desenvolver a capacidade de validação antes de investir é essencial. Visitar unidades e conversar com franqueados são passos importantes.',
          medium: 'Você já pensa em validar sua decisão. Nossa consultoria pode otimizar esse processo para você.',
          high: 'Excelente capacidade de análise! Você sabe exatamente como validar uma oportunidade de franquia antes de investir.'
        },
        perfil_momento_vida: {
          low: 'Alinhar seus objetivos e momento de vida antes de investir vai garantir uma decisão mais satisfatória a longo prazo.',
          medium: 'Você está em um bom momento. Com clareza de objetivos, a escolha da franquia certa se torna muito mais natural.',
          high: 'Seu perfil e momento de vida são ideais para investir em uma franquia! Você tem os ingredientes certos para o sucesso.'
        }
      }
    }
  }
};
